<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useI18n } from "vue-i18n";
import { resolveStorybookAsset } from "@/lib/storybook-assets";
import { capture } from "@/lib/posthog";

const { t } = useI18n();

interface StorybookBeat {
  index: number;
  slug: string;
}

const BEATS: StorybookBeat[] = [
  { index: 1, slug: "messy-morning" },
  { index: 2, slug: "cresci-steps-in" },
  { index: 3, slug: "parent-configures" },
  { index: 4, slug: "kid-pauses" },
  { index: 5, slug: "kid-checks" },
  { index: 6, slug: "tidy-morning" },
];

const activeIndex = ref(0);
const activeBeat = computed(() => BEATS[activeIndex.value]!);

function goTo(index: number) {
  activeIndex.value = index;
  pauseAutoAdvance();
}

function goNext() {
  activeIndex.value = (activeIndex.value + 1) % BEATS.length;
  pauseAutoAdvance();
}

function goPrev() {
  activeIndex.value = (activeIndex.value + BEATS.length - 1) % BEATS.length;
  pauseAutoAdvance();
}

// --- Auto-advance ---
const AUTO_INTERVAL = 9000;
const IDLE_RESUME_DELAY = 12000;

let autoTimer: ReturnType<typeof setInterval> | null = null;
let resumeTimer: ReturnType<typeof setTimeout> | null = null;
const isAutoPlaying = ref(true);
const sectionRef = ref<HTMLElement | null>(null);
const isSectionVisible = ref(false);
const isPrefersReducedMotion = ref(false);

function startAutoAdvance() {
  stopAutoAdvance();
  if (isPrefersReducedMotion.value || !isSectionVisible.value) return;
  autoTimer = setInterval(() => {
    activeIndex.value = (activeIndex.value + 1) % BEATS.length;
  }, AUTO_INTERVAL);
  isAutoPlaying.value = true;
}

function stopAutoAdvance() {
  if (autoTimer !== null) {
    clearInterval(autoTimer);
    autoTimer = null;
  }
  isAutoPlaying.value = false;
}

function pauseAutoAdvance() {
  stopAutoAdvance();
  clearResumeTimer();
  resumeTimer = setTimeout(() => {
    startAutoAdvance();
  }, IDLE_RESUME_DELAY);
}

function clearResumeTimer() {
  if (resumeTimer !== null) {
    clearTimeout(resumeTimer);
    resumeTimer = null;
  }
}

function handleFocusIn() {
  stopAutoAdvance();
  clearResumeTimer();
}

function handleFocusOut() {
  pauseAutoAdvance();
}

// --- Swipe detection ---
let pointerStartX = 0;
let pointerStartY = 0;
let isSwiping = false;
const SWIPE_THRESHOLD = 50;

function handlePointerDown(e: PointerEvent) {
  pointerStartX = e.clientX;
  pointerStartY = e.clientY;
  isSwiping = true;
}

function handlePointerMove(e: PointerEvent) {
  if (!isSwiping) return;
  const dx = e.clientX - pointerStartX;
  if (Math.abs(dx) > 10) {
    e.preventDefault();
  }
}

function handlePointerUp(e: PointerEvent) {
  if (!isSwiping) return;
  isSwiping = false;
  const dx = e.clientX - pointerStartX;
  const dy = e.clientY - pointerStartY;
  if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
    if (dx < 0) goNext();
    else goPrev();
  }
}

// --- Keyboard navigation ---
function handleKeydown(e: KeyboardEvent) {
  if (e.key === "ArrowRight") {
    e.preventDefault();
    goNext();
  } else if (e.key === "ArrowLeft") {
    e.preventDefault();
    goPrev();
  }
}

// --- Visibility observer ---
let visibilityObserver: IntersectionObserver | null = null;

function handleVisibilityChange() {
  if (document.hidden) {
    stopAutoAdvance();
    clearResumeTimer();
  } else if (isSectionVisible.value) {
    startAutoAdvance();
  }
}

onMounted(() => {
  isPrefersReducedMotion.value = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;

  const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
  const motionHandler = (e: MediaQueryListEvent) => {
    isPrefersReducedMotion.value = e.matches;
    if (e.matches) stopAutoAdvance();
    else if (isSectionVisible.value) startAutoAdvance();
  };
  mql.addEventListener("change", motionHandler);

  if (sectionRef.value) {
    visibilityObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) isSectionVisible.value = entry.isIntersecting;
      },
      { threshold: 0.2 },
    );
    visibilityObserver.observe(sectionRef.value);
  }

  document.addEventListener("visibilitychange", handleVisibilityChange);
});

watch(isSectionVisible, (visible) => {
  if (visible && !isPrefersReducedMotion.value) {
    startAutoAdvance();
  } else {
    stopAutoAdvance();
    clearResumeTimer();
  }
});

watch(activeIndex, (newIdx) => {
  capture("storybook_beat_viewed", { index: newIdx + 1 });
});

onBeforeUnmount(() => {
  stopAutoAdvance();
  clearResumeTimer();
  visibilityObserver?.disconnect();
  document.removeEventListener("visibilitychange", handleVisibilityChange);
});
</script>

<template>
  <section
    id="the-morning-that-runs-itself"
    ref="sectionRef"
    class="bg-[var(--color-bg-secondary)] px-5 py-12 md:px-12 md:py-24"
    aria-roledescription="carousel"
    :aria-label="t('storybook.carouselLabel')"
    @focusin="handleFocusIn"
    @focusout="handleFocusOut"
    @keydown="handleKeydown"
  >
    <div class="mx-auto max-w-[var(--max-w-page)]">
      <div class="storybook-heading-wrap">
        <h2 data-reveal class="fade-up storybook-heading">
          {{ t('storybook.heading') }}
        </h2>
        <span
          data-reveal
          data-reveal-delay="1"
          class="storybook-heading-hairline"
          aria-hidden="true"
        ></span>
      </div>

      <div
        data-reveal
        data-reveal-delay="2"
        class="fade-up-lg storybook-frame"
        tabindex="0"
        @pointerdown="handlePointerDown"
        @pointermove="handlePointerMove"
        @pointerup="handlePointerUp"
      >
        <img
          v-for="beat in BEATS"
          :key="beat.slug"
          :src="resolveStorybookAsset(beat.index, beat.slug)"
          :alt="t(`storybook.beats.${beat.index}.alt`)"
          class="storybook-image"
          :class="{ 'is-active': beat.index - 1 === activeIndex }"
          :loading="beat.index === 1 ? 'eager' : 'lazy'"
          :fetchpriority="beat.index === 1 ? 'high' : 'auto'"
          decoding="async"
          draggable="false"
        />
      </div>

      <div
        class="storybook-caption"
        aria-live="polite"
        data-reveal
        data-reveal-delay="3"
      >
        <p :key="activeIndex" class="storybook-caption__text">
          {{ t(`storybook.beats.${activeBeat.index}.caption`) }}
        </p>
      </div>

      <nav
        class="storybook-nav"
        :aria-label="t('storybook.carouselLabel')"
        data-reveal
        data-reveal-delay="4"
      >
        <button
          class="nav-arrow hidden md:flex"
          :aria-label="t('storybook.prevBeat')"
          @click="goPrev"
        >
          <UIcon name="i-lucide-arrow-left" class="size-4" />
        </button>

        <div class="storybook-dots">
          <button
            v-for="(beat, i) in BEATS"
            :key="beat.slug"
            class="nav-dot"
            :class="{ 'is-active': i === activeIndex }"
            :aria-label="t('storybook.goToBeat', { n: beat.index })"
            :aria-pressed="i === activeIndex"
            @click="goTo(i)"
          />
        </div>

        <button
          class="nav-arrow hidden md:flex"
          :aria-label="t('storybook.nextBeat')"
          @click="goNext"
        >
          <UIcon name="i-lucide-arrow-right" class="size-4" />
        </button>
      </nav>
    </div>
  </section>
</template>

<style scoped>
/* Heading + gold hairline */
.storybook-heading-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
}

@media (min-width: 768px) {
  .storybook-heading-wrap {
    margin-bottom: 32px;
  }
}

.storybook-heading {
  font-family: var(--font-display);
  font-weight: 500;
  text-align: center;
  font-size: 1.5rem; /* text-2xl */
  line-height: 2rem;
  color: var(--color-text-primary);
}

@media (min-width: 768px) {
  .storybook-heading {
    font-size: 2.25rem; /* text-4xl */
    line-height: 2.5rem;
  }
}

.storybook-heading-hairline {
  display: block;
  width: 32px;
  height: 2px;
  background: color-mix(in srgb, var(--color-accent-gold) 60%, transparent);
  margin: 12px auto 0;
}

@media (min-width: 768px) {
  .storybook-heading-hairline {
    margin: 16px auto 0;
  }
}

/* Frame */
.storybook-frame {
  position: relative;
  aspect-ratio: 16 / 9;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: var(--shadow-card);
  touch-action: pan-y pinch-zoom;
  background: var(--color-surface);
  margin: 0 auto;
  width: 100%;
}

@media (min-width: 768px) {
  .storybook-frame {
    max-width: 880px;
  }
}

.storybook-frame::before {
  content: "";
  position: absolute;
  inset: 8px;
  border: 1px solid color-mix(in srgb, var(--color-border) 70%, transparent);
  border-radius: calc(1rem - 8px);
  pointer-events: none;
  z-index: 2;
}

.storybook-frame:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

/* Stacked images */
.storybook-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  pointer-events: none;
  z-index: 1;
}

.storybook-image.is-active {
  opacity: 1;
  pointer-events: auto;
}

@media (prefers-reduced-motion: no-preference) {
  .storybook-image {
    transition: opacity 500ms var(--ease-smooth);
  }
}

/* Caption block below frame */
.storybook-caption {
  margin: 24px auto 0;
  width: 100%;
  max-width: 720px;
  padding-top: 16px;
  border-top: 2px solid var(--color-accent-gold);
}

@media (min-width: 768px) {
  .storybook-caption {
    margin-top: 32px;
    padding-top: 20px;
  }
}

.storybook-caption__text {
  font-family: var(--font-body);
  font-weight: 500;
  font-size: 17px;
  color: var(--color-text-primary);
  line-height: 1.55;
  text-align: center;
}

@media (min-width: 768px) {
  .storybook-caption__text {
    font-size: 20px;
    line-height: 1.5;
  }
}

@media (prefers-reduced-motion: no-preference) {
  .storybook-caption__text {
    animation: caption-in 260ms ease-out 80ms both;
  }
}

@keyframes caption-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Navigation */
.storybook-nav {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 24px;
}

@media (min-width: 768px) {
  .storybook-nav {
    margin-top: 32px;
  }
}

.storybook-dots {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Dots — 32px hit area via padding + content-box clip */
.nav-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color-text-muted);
  opacity: 0.4;
  border: none;
  padding: 12px;
  background-clip: content-box;
  cursor: pointer;
  box-sizing: content-box;
}

.nav-dot.is-active {
  opacity: 1;
  background: var(--color-accent-gold);
  background-clip: content-box;
  width: 10px;
  height: 10px;
}

.nav-dot:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

@media (prefers-reduced-motion: no-preference) {
  .nav-dot {
    transition:
      opacity 200ms var(--ease-smooth),
      width 200ms var(--ease-smooth),
      height 200ms var(--ease-smooth),
      background 200ms var(--ease-smooth);
  }
}

/* Arrow buttons */
.nav-arrow {
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  cursor: pointer;
  padding: 0;
}

@media (prefers-reduced-motion: no-preference) {
  .nav-arrow {
    transition:
      border-color 150ms ease,
      color 150ms ease;
  }
}

.nav-arrow:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.nav-arrow:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}
</style>
